"use client";

import { css, cx } from "styled-system/css";

type CalView = "timeGridDay" | "timeGridWeek" | "dayGridMonth" | "listWeek";

const VIEW_OPTIONS_DESKTOP: { value: CalView; label: string }[] = [
  { value: "timeGridDay", label: "Día" },
  { value: "timeGridWeek", label: "Semana" },
  { value: "dayGridMonth", label: "Mes" },
  { value: "listWeek", label: "Agenda" },
];

/** En mobile solo vistas que no aprietan el calendario */
const VIEW_OPTIONS_MOBILE: { value: CalView; label: string }[] = [
  { value: "listWeek", label: "Agenda" },
  { value: "timeGridDay", label: "Día" },
];

export type StaffFilterOption = { id: string; name: string; color: string | null };

export function CalendarHeader({
  title,
  view,
  onPrev,
  onNext,
  onToday,
  onView,
  onNew,
  embedded,
  staffFilter,
}: {
  title: string;
  view: CalView;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onView: (v: CalView) => void;
  onNew: () => void;
  /** Sin margen inferior (ej. dentro del panel del calendario) */
  embedded?: boolean;
  /** Filtro por profesional de agenda (AgendaStaff). Se omite si no hay staff cargado. */
  staffFilter?: {
    options: StaffFilterOption[];
    value: string;
    onChange: (id: string) => void;
  };
}) {
  const pill = (active: boolean) =>
    cx(
      css({
        px: "3",
        py: "2",
        rounded: "lg",
        fontSize: "sm",
        fontWeight: "semibold",
        borderWidth: "1px",
        borderColor: active ? "rgba(99,102,241,0.6)" : "#e2e8f0",
        bg: active ? "#eef2ff" : "white",
        color: active ? "#3730a3" : "#334155",
        cursor: "pointer",
        _hover: { bg: active ? "#eef2ff" : "#f8fafc" },
      }),
    );

  const iconBtn = css({
    w: "10",
    h: "10",
    rounded: "lg",
    borderWidth: "1px",
    borderColor: "#e2e8f0",
    bg: "white",
    cursor: "pointer",
    _hover: { bg: "#f8fafc" },
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#334155",
    fontWeight: "bold",
    flexShrink: 0,
  });

  const newBtn = css({
    px: "4",
    py: "2.5",
    rounded: "lg",
    fontSize: "sm",
    fontWeight: "semibold",
    color: "white",
    border: "none",
    cursor: "pointer",
    bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
    whiteSpace: "nowrap",
  });

  return (
    <div
      className={css({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "3",
        flexWrap: "wrap",
        mb: embedded ? "0" : "4",
      })}
    >
      {/* Navegación temporal */}
      <div className={css({ display: "flex", alignItems: "center", gap: "2", flexWrap: "wrap", minW: 0 })}>
        <button type="button" className={iconBtn} onClick={onPrev} aria-label="Anterior">
          ←
        </button>
        <button type="button" className={iconBtn} onClick={onToday} aria-label="Hoy">
          Hoy
        </button>
        <button type="button" className={iconBtn} onClick={onNext} aria-label="Siguiente">
          →
        </button>
        <span
          className={css({
            fontSize: { base: "md", sm: "lg" },
            fontWeight: "bold",
            color: "#0f172a",
            ml: "1",
            minW: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          })}
        >
          {title}
        </span>
      </div>

      {/* Controles de vista + nuevo turno */}
      <div className={css({ display: "flex", gap: "2", alignItems: "center", flexWrap: "wrap" })}>
        {/* Select de vista — solo visible en mobile (solo Agenda y Día) */}
        <select
          className={css({
            display: { base: "block", sm: "none" },
            py: "2",
            px: "3",
            rounded: "lg",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            bg: "white",
            fontSize: "sm",
            fontWeight: "semibold",
            color: "#334155",
            cursor: "pointer",
          })}
          value={VIEW_OPTIONS_MOBILE.some((o) => o.value === view) ? view : "listWeek"}
          onChange={(e) => onView(e.target.value as CalView)}
          aria-label="Vista del calendario"
        >
          {VIEW_OPTIONS_MOBILE.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        {/* Pills de vista — solo visibles en sm+ */}
        <div className={css({ display: { base: "none", sm: "flex" }, gap: "2", alignItems: "center" })}>
          {VIEW_OPTIONS_DESKTOP.map((o) => (
            <button key={o.value} type="button" className={pill(view === o.value)} onClick={() => onView(o.value)}>
              {o.label}
            </button>
          ))}
        </div>

        {staffFilter && staffFilter.options.length > 0 ? (() => {
          const selected = staffFilter.options.find((o) => o.id === staffFilter.value);
          return (
            <div
              className={css({
                display: "flex",
                alignItems: "center",
                gap: "2",
                px: "3",
                py: "2",
                rounded: "lg",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                bg: "white",
              })}
            >
              <span
                className={css({ w: "2.5", h: "2.5", rounded: "full", flexShrink: 0, borderWidth: selected?.color ? "1px" : "0", borderColor: "rgba(0,0,0,0.1)" })}
                style={{ backgroundColor: selected?.color ?? "#cbd5e1" }}
              />
              <select
                className={css({
                  border: "none",
                  outline: "none",
                  bg: "transparent",
                  fontSize: "sm",
                  fontWeight: "semibold",
                  color: "#334155",
                  cursor: "pointer",
                })}
                value={staffFilter.value}
                onChange={(e) => staffFilter.onChange(e.target.value)}
                aria-label="Filtrar por profesional"
              >
                <option value="">Todos los profesionales</option>
                {staffFilter.options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
          );
        })() : null}

        <button type="button" onClick={onNew} className={newBtn}>
          + Nuevo turno
        </button>
      </div>
    </div>
  );
}
